import { getCurrentOrg } from "#webapp/src/util/org.util";
import { useQuery } from "@apollo/client";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { Nav } from "@clockwise/web-commons/src/components/nav";
import { useIsOutlook } from "@clockwise/web-commons/src/util/OutlookContext";
import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IViewer } from "../../__schema__";
import { setMenuDrawerOpen } from "../../state/actions/chrome-extension.actions";
import { IReduxState } from "../../state/reducers/root.reducer";
import { logger } from "../../util/logger.util";
import { ChromeMenuDrawer } from "../chrome-menu-drawer";
import { EventRenderer } from "../event/EventRenderer";
import { RouteRenderer } from "../route-renderer";
import { PlannerContext } from "../web-app-calendar/Context";
import { OutlookExistingEventQueryDocument } from "./__generated__/OutlookExistingEvent.generated";
import { OutlookMetadataQueryDocument } from "./__generated__/OutlookMetadata.generated";

// interface Props {}
export const OutlookExistingEvent = () => {
  const isOutlook = useIsOutlook();

  const dispatch = useDispatch();
  const menuDrawerOpen = useSelector((state: IReduxState) => state.webExtension.menuDrawerOpen);
  const [m365EventId, setM365EventId] = React.useState<string | undefined>(undefined);

  // If we do not have an event Id, we are creating a new event.
  // Send to /outlook/new-event.
  // Outlook doesn't provide a way to differentiate between creating a new event and editing an existing event.
  useEffect(() => {
    // Must use window.Office.context.mailbox?.item?.itemId to get id while viewing an event,
    // but must use window.Office.context.mailbox?.item?.getItemIdAsync to get id while editing and existing event. Shrug.
    const maybeId = window.Office.context.mailbox?.item?.itemId;
    if (maybeId) {
      setM365EventId(window.Office.context.mailbox.convertToRestId(maybeId, "v2.0"));
      return;
    }
    window.Office.context.mailbox?.item?.getItemIdAsync?.((result) => {
      if (result.status === window.Office.AsyncResultStatus.Succeeded) {
        setM365EventId(window.Office.context.mailbox.convertToRestId(result.value, "v2.0"));
      } else {
        setM365EventId("");
        logger.error("Failed to get event id in OutlookExistingEvent", result);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, loading: metadataLoading, error } = useQuery(OutlookMetadataQueryDocument);

  const viewer = getValue(data?.viewer, "Viewer");
  const org = getCurrentOrg(viewer);
  const calendarId = org?.primaryOrgCalendar;

  const { data: eventData, loading: eventLoading } = useQuery(OutlookExistingEventQueryDocument, {
    skip: !m365EventId || !calendarId,
    variables: {
      orgId: org?.id || "",
      m365EventId: m365EventId || "",
      calendarId: calendarId || "",
    },
  });

  // Get the externalEventId from the event data.
  const externalEventId = getValue(getValue(eventData?.org, "Org")?.m365Event, "EventParent")
    ?.events?.[0]?.eventKey?.externalEventId;

  const loading = m365EventId === undefined || metadataLoading || eventLoading;
  const noEventId = !loading && !externalEventId;

  const activeEvent = {
    externalEventId: externalEventId || "",
    calendarId: calendarId || "",
  };

  return (
    <RouteRenderer loading={loading} error={!isOutlook || !!error} notFound={noEventId}>
      <PlannerContext.Provider value={{}}>
        <>
          <ChromeMenuDrawer
            viewer={viewer as IViewer}
            open={menuDrawerOpen}
            style={{ zIndex: 30 }}
          />
          <Nav
            showMenu={true}
            showCollapse={false}
            onClickCollapse={() => null}
            onClickMenu={() => dispatch(setMenuDrawerOpen(true))}
          />
          <div className="cw-pr-1 cw-h-full cw-w-full cw-mt-2 cw-mb-1">
            <EventRenderer event={activeEvent} />
          </div>
        </>
      </PlannerContext.Provider>
    </RouteRenderer>
  );
};
